import { create } from 'zustand';
import type {
  Workspace,
  CreateWorkspacePayload,
  UpdateWorkspacePayload,
} from '@/types/workspace.types';
import workspacesApi from '@/api/workspaces.api';

interface WorkspaceState {
  workspaces: Workspace[];
  currentWorkspace: Workspace | null;
  isLoading: boolean;
  error: string | null;

  fetchWorkspaces: () => Promise<void>;
  selectWorkspace: (workspaceId: string) => Promise<void>;
  createWorkspace: (payload: CreateWorkspacePayload) => Promise<Workspace>;
  updateWorkspace: (id: string, payload: UpdateWorkspacePayload) => Promise<void>;
  deleteWorkspace: (id: string) => Promise<void>;
  clearCurrent: () => void;
}

export const useWorkspaceStore = create<WorkspaceState>((set, get) => ({
  workspaces: [],
  currentWorkspace: null,
  isLoading: false,
  error: null,

  fetchWorkspaces: async () => {
    set({ isLoading: true, error: null });
    try {
      const workspaces = await workspacesApi.list();
      set({ workspaces, isLoading: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch workspaces';
      set({ error: message, isLoading: false });
    }
  },

  selectWorkspace: async (workspaceId: string) => {
    set({ isLoading: true, error: null });
    try {
      const workspace = await workspacesApi.getById(workspaceId);
      set({ currentWorkspace: workspace, isLoading: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load workspace';
      set({ error: message, isLoading: false });
    }
  },

  createWorkspace: async (payload: CreateWorkspacePayload) => {
    const workspace = await workspacesApi.create(payload);
    set({ workspaces: [...get().workspaces, workspace] });
    return workspace;
  },

  updateWorkspace: async (id: string, payload: UpdateWorkspacePayload) => {
    const updated = await workspacesApi.update(id, payload);
    set({
      workspaces: get().workspaces.map((w) => (w.id === id ? updated : w)),
      currentWorkspace: get().currentWorkspace?.id === id ? updated : get().currentWorkspace,
    });
  },

  deleteWorkspace: async (id: string) => {
    await workspacesApi.delete(id);
    set({
      workspaces: get().workspaces.filter((w) => w.id !== id),
      currentWorkspace: get().currentWorkspace?.id === id ? null : get().currentWorkspace,
    });
  },

  clearCurrent: () => set({ currentWorkspace: null }),
}));

export default useWorkspaceStore;
